<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />
            <!-- Type de la formule -->
            <div class="ppv2-mb-6" v-if="false">
                <SelectableList
                    :label="$t('package.step1.typeLabel')"
                    v-model="selectedType"
                    :options="typeOptions"
                    class="ppv2-mb-6"
                    @input="clearError('type')"
                />
                <div v-if="errors.type" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-[-15px] ppv2-mb-4">
                    {{ errors.type }}
                </div>
            </div>

            <!-- Nom de la formule + Nom interne -->
            <div class="ppv2-flex ppv2-gap-4">
                <div class="ppv2-flex-1">
                    <CustomInput
                        :label="$t('package.step2.nameLabel')"
                        :required="true"
                        v-model="formData.name"
                        :placeholder="$t('package.step2.namePlaceholder')"
                        :state="getState('name')"
                        :errorText="errors.name"
                        @input="clearError('name')"
                        @focus="activeField = 'name'"
                        @blur="activeField = null"
                    />
                </div>
                <div class="ppv2-flex-1">
                    <CustomInput
                        :label="$t('package.step2.internalNameLabel')"
                        :required="false"
                        v-model="formData.internal_name"
                        :placeholder="$t('package.step2.internalNamePlaceholder')"
                        :state="getState('internal_name')"
                        :errorText="errors.internal_name"
                        @input="clearError('internal_name')"
                        @focus="activeField = 'internal_name'"
                        @blur="activeField = null"
                    />
                </div>
            </div>

            <!-- Description -->
            <BaseTextarea
                :label="$t('package.step2.descriptionLabel')"
                v-model="formData.description"
                :placeholder="$t('package.step2.descriptionPlaceholder')"
                :useEditor="true"
                @focus="setActiveField('description')"
                @blur="clearActiveField('description')"
                iconIA
                :rows="5"
                :state="getState('description')"
                :error="errors.description"
                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                customLabelClass="poppinsLight ppv2-text-black"
                classContainerAdd="ppv2-mb-[20px]"
                :required="false"
            />

            <!-- Image Upload -->
            <div class="upload-container">
                <CustomInputUpload
                    :label="$t('package.step2.imageLabel')"
                    v-model="formData.image"
                    :required="false"
                    :state="getState('image')"
                    :errorText="errors.image"
                    @input="onFileSelected"
                />
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': !isCreationMode }">
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ isCreationMode ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <!-- Preview Panel -->
        <PreviewPackage :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import HeaderStep from './HeaderStep.vue'
import PreviewPackage from './PreviewPackage.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import SelectableList from '../../../Shared/SelectableList.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'PackageStep2',
    components: {
        HeaderStep,
        PreviewPackage,
        CustomInput,
        BaseTextarea,
        CustomInputUpload,
        BaseButton,
        BackButton,
        SelectableList,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        isCreationMode: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            initialFormData: null,
            typeOptions: [
                {
                    label: this.$t('package.step1.types.specific.label'),
                    value: 'specificPrestation',
                    subtitle: this.$t('package.step1.types.specific.subtitle'),
                },
                {
                    label: this.$t('package.step1.types.family.label'),
                    value: 'family',
                    subtitle: this.$t('package.step1.types.family.subtitle'),
                },
            ],
        }
    },
    computed: {
        ...mapGetters('package', ['formData', 'isEditMode', 'isDirty', 'loading']),
        formData: {
            get() {
                return this.$store.getters['package/formData']
            },
            set(value) {
                this.$store.dispatch('package/setFormData', value)
            },
        },
        selectedType: {
            get() {
                return this.$store.getters['package/selectedType']
            },
            set(value) {
                this.$store.dispatch('package/setSelectedType', value)
            },
        },
    },
    mounted() {
        this.initialFormData = JSON.stringify(this.formData)
    },
    methods: {
        normalizeFontSize,
        ...mapActions('package', ['setFormData', 'savePackage', 'setSelectedType']),
        normalizeSize,

        clearError(field) {
            if (this.errors[field]) {
                delete this.errors[field]
            }
        },

        setActiveField(field) {
            this.activeField = field
        },

        clearActiveField(field) {
            if (this.activeField === field) {
                this.activeField = null
            }
        },

        onFileSelected(file) {
            if (!file) return

            const reader = new FileReader()
            reader.onload = () => {
                this.setFormData({
                    image: reader.result,
                    imagePreview: reader.result,
                })
            }
            reader.readAsDataURL(file)
        },

        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.formData[field]) return 'valid'
            return 'default'
        },

        hasChanges() {
            return JSON.stringify(this.formData) !== this.initialFormData
        },

        resetInitialState() {
            this.initialFormData = JSON.stringify(this.formData)
        },

        async submitForm() {
            this.errors = {}

            if (!this.formData.name || !this.formData.name.trim()) {
                this.errors.name = this.$t('package.step2.errors.nameRequired')
                return
            }

            if (!this.selectedType) {
                this.errors.type = this.$t('package.step1.errors.typeRequired')
                return
            }

            // Save to API first, then emit next-step
            try {
                await this.savePackage()
                this.resetInitialState()
                this.$toast.success(
                    this.id ? this.$t('package.notifications.success_update') : this.$t('package.notifications.success_add')
                )
                if (this.isCreationMode) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.savePackage()
                this.resetInitialState()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving package:', error)
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
    float: right;
}

.upload-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
}

.upload-container:hover,
.upload-container:focus,
.upload-container:active {
    opacity: 1;
}
</style>
